import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ExternalLink, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";

/**
 * Cross-project retention claims dashboard.
 * Lists all completed projects with outstanding retention (held − claimed > 0),
 * sorted: overdue first (by days overdue desc), then upcoming (by due date asc),
 * then unscheduled (no retention_period_days).
 *
 * Row click → callback to open IncomeLedgerDialog for that project so BOD/FAT
 * can record the Retention Claim entry directly.
 */
export default function RetentionClaimsDialog({ open, onOpenChange, onOpenLedger }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/active-projects")
      .then(({ data }) => {
        const now = Date.now();
        const acc = [];
        for (const p of data || []) {
          if (p.project_type === "internal") continue;
          if (p.status !== "completed") continue;
          const held = (p.income_entries || []).reduce((s, e) => s + (e.retention_amount != null ? Number(e.retention_amount) : 0), 0);
          const claimed = (p.income_entries || []).reduce((s, e) => s + (e.is_retention_claim ? Number(e.amount || 0) : 0), 0);
          const outstanding = Math.max(0, held - claimed);
          if (outstanding <= 0.01) continue;
          const days = Number(p.retention_period_days) || 0;
          let dueAt = null;
          let overdue = false;
          let daysDelta = null;
          if (days > 0 && p.completed_at) {
            const d = new Date(p.completed_at);
            d.setDate(d.getDate() + days);
            dueAt = d;
            const diffMs = d.getTime() - now;
            daysDelta = Math.round(diffMs / (1000 * 60 * 60 * 24));
            overdue = diffMs < 0;
          }
          acc.push({
            id: p.id,
            project: p,
            project_name: p.project_name,
            client: p.client,
            completed_at: p.completed_at,
            held,
            claimed,
            outstanding,
            retention_period_days: days,
            due_at: dueAt,
            overdue,
            days_delta: daysDelta,
          });
        }
        acc.sort((a, b) => {
          // Overdue first, more overdue first
          if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
          if (a.overdue && b.overdue) return (a.days_delta ?? 0) - (b.days_delta ?? 0);
          // Then upcoming by due_at asc
          if (a.due_at && b.due_at) return a.due_at.getTime() - b.due_at.getTime();
          if (a.due_at) return -1;
          if (b.due_at) return 1;
          // Unscheduled last, alphabetical
          return (a.project_name || "").localeCompare(b.project_name || "");
        });
        setRows(acc);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => (r.project_name || "").toLowerCase().includes(s) || (r.client || "").toLowerCase().includes(s));
  }, [rows, q]);

  const totals = useMemo(() => {
    const overdueSum = rows.filter((r) => r.overdue).reduce((s, r) => s + r.outstanding, 0);
    const pendingSum = rows.filter((r) => !r.overdue).reduce((s, r) => s + r.outstanding, 0);
    return {
      overdue: overdueSum,
      pending: pendingSum,
      total: overdueSum + pendingSum,
      overdueCount: rows.filter((r) => r.overdue).length,
      pendingCount: rows.filter((r) => !r.overdue).length,
    };
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="dialog-retention-claims">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Retention Claims Dashboard</DialogTitle>
          <DialogDescription>
            Semua proyek selesai yang masih memiliki retensi belum diklaim. Klik baris untuk buka Income Ledger dan catat klaim.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="retention-claims-summary">
          <div className="bg-rose-50 border border-rose-200 p-3">
            <div className="overline text-rose-700">Terlambat</div>
            <div data-testid="retention-overdue-total" className="font-display text-lg tabular-nums text-rose-800">{formatIDR(totals.overdue)}</div>
            <div className="text-[10px] text-rose-600">{totals.overdueCount} proyek</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3">
            <div className="overline text-amber-700">Pending</div>
            <div data-testid="retention-pending-total" className="font-display text-lg tabular-nums text-amber-800">{formatIDR(totals.pending)}</div>
            <div className="text-[10px] text-amber-600">{totals.pendingCount} proyek</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 p-3">
            <div className="overline text-slate-600">Total Outstanding</div>
            <div className="font-display text-lg tabular-nums text-[#0B1D3A]">{formatIDR(totals.total)}</div>
            <div className="text-[10px] text-slate-500">{rows.length} proyek</div>
          </div>
        </div>

        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="retention-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none pl-9 h-9 text-xs"
            placeholder="Cari nama proyek atau klien…"
          />
        </div>

        <div className="border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Proyek", "Klien", "Selesai", "Held", "Claimed", "Outstanding", "Jatuh Tempo", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2 overline text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="retention-claims-tbody">
              {loading && (
                <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">Memuat…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">
                  {q.trim() ? "Tidak ada proyek yang cocok pencarian." : "Semua retensi sudah diklaim atau belum ada proyek selesai."}
                </td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`retention-row-${r.id}`}
                  className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${r.overdue ? "bg-rose-50/40 border-l-4 border-l-rose-500" : ""}`}
                  onClick={() => {
                    onOpenChange(false);
                    onOpenLedger?.(r.project);
                  }}
                >
                  <td className="px-3 py-2 font-semibold text-[#0B1D3A]">{r.project_name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.client || "-"}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-500">{r.completed_at ? fmtDate(r.completed_at) : "-"}</td>
                  <td className="px-3 py-2 tabular-nums text-amber-700">{formatIDR(r.held)}</td>
                  <td className="px-3 py-2 tabular-nums text-emerald-700">{formatIDR(r.claimed)}</td>
                  <td className="px-3 py-2 tabular-nums font-semibold text-rose-700">{formatIDR(r.outstanding)}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {r.due_at ? (
                      <div>
                        {fmtDate(r.due_at.toISOString())}
                        {r.days_delta != null && (
                          <div className={`text-[10px] ${r.overdue ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                            {r.overdue ? `${Math.abs(r.days_delta)} hari lewat` : `${r.days_delta} hari lagi`}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-slate-400 italic text-[10px]">Belum diset</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.overdue ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-rose-400 bg-rose-500 text-white">
                        <AlertTriangle size={10} /> Overdue
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-amber-400 bg-amber-100 text-amber-800">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="inline-flex items-center gap-1 text-slate-500 text-[10px] uppercase tracking-wider font-semibold">
                      Klaim <ExternalLink size={11} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
